import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { OpUnitType } from "dayjs";
import dayjs from "dayjs";
import { capitalize } from "lodash";

import { SWReactIcons } from "@similarweb/icons";
import { shortMonthAndDay, formatDate } from "@similarweb/pro-logic-components";
import { ResizableContainer, Switcher, Dropdown, DropdownButton } from "@similarweb/ui-components";

import { pngDownload } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { swSettings } from "app/common/services/swSettings";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import DownloadButtonMenu from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { WeShare } from "app/components/weshare/weshare";
import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { BasicDurations } from "app/services/DurationService";
import { translate } from "app/filters/i18nFilter";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { ChartLabels } from "./ChartLabels";
import { getChartConfig } from "./ChartConfig";
import type { IGraphTitles } from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";
import { useGraphTooltip } from "./useGraphTooltip";

import { Loader } from "./Loader";

import {
    BoxSubtitleStyled,
    BoxTitleContainer,
    ChartContainerNoData,
    CircleSwitcherItemStyled,
    ClicksOverTimeTitle,
    Divider,
    GraphHeaderActions,
    LoaderContainer,
    TooltipStyles,
    ChartFilterBar,
    SimpleDropdownItemStyled,
    DropdownButtonIconWrapper,
    LegendsContainer,
    Container,
    ChartContainerWrapper,
} from "app/components/keyword-research/clicks-over-time-graph/styledComponents";
import { EGraphType, GRAPH_TYPES } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import { graphTypeToExtraFiledType, useGraphData } from "./UseGraphData";

import type { ITransformedDataPoint } from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";
import { getIndexOfList } from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";

import { MetricContainerStyled } from "../../pages/serp-players/styledComponents";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import {
    DASHBOARD_TYPE,
    DEFAULT_GRAPH_TYPE,
    GRAPH_GRANULARITIES,
    GRAPH_GRANULARITY_TO_TRACKING_NAME,
    GRAPH_TYPE_TO_TRACKING_NAME,
    graphTypeTooltips,
    THIRTEEN_MONTHS_INDEX,
    TIME_GRANULARITY,
    TWENTY_EIGHT_DAYS_OR_1_MONTH_INDEX,
} from "app/common/keyword-research/clicks-over-time-graph/graphConsts";

import { openHooksModalV5 } from "app/services/ModalService";

import type { IPageFilters } from "../../pages/serp-players/FiltersSection/Types";
import { useAllKeywordGroups } from "app/services/keyword-groups";
import { EGraphGranularities, graphGranularityToString } from "app/common/keyword-research/serp-players/consts";
import {
    isDurationSwitcherDisabled,
    isDurationSwitcherLocked,
    updateGraphGranularityValue,
} from "app/common/keyword-research/keywords-overview/trendOverTimeTimeframeHelpers";

interface IGraphProps {
    isKeywordsGroup: boolean;
    filters: Partial<IPageFilters>;
    endpoint: string;
    dashboardMetric: string;
    displayTooltipOnLegendOver?: boolean;
    graphTitles?: IGraphTitles;
    excelEndpoint?: string;
    selectedSiteLegend?: string;
}
const isGranularityDisabled = (granularity, selectedGraphDurationIndex) => {
    if (
        selectedGraphDurationIndex === TWENTY_EIGHT_DAYS_OR_1_MONTH_INDEX &&
        granularity === toggleItems.Monthly.title
    ) {
        return true;
    } else if (
        selectedGraphDurationIndex === THIRTEEN_MONTHS_INDEX &&
        (granularity === toggleItems.Daily.title || granularity === toggleItems.Weekly.title)
    ) {
        return true;
    }
    return false;
};

const getTimeGranularityForApi = (timeGranularityParam: string) =>
    timeGranularityParam ? getIndexOfList(graphGranularityToString, timeGranularityParam) : EGraphGranularities.WEEKLY;

interface IDateFilter {
    filter: string;
    value: {
        from: number;
        to: number;
    };
}
export const defaultGraphTitles = {
    inputPlaceholder: "common.placeholder.search.domain",
    subTitleSelected: "keywordanalysis.organic2.clicks.over.time.domains.selected",
    labelTitle: "keywordanalysis.organic2.clicks.over.time.domain",
    tooltipTitle: "keywordanalysis.organic2.clicks.over.time.tooltip.domain",
};
export const Graph: FC<IGraphProps> = ({
    isKeywordsGroup,
    filters,
    endpoint,
    displayTooltipOnLegendOver,
    dashboardMetric,
    graphTitles = defaultGraphTitles,
    excelEndpoint,
}) => {
    const graphContainerRef = useRef<HTMLDivElement>(null);
    const { params } = useCurrentStateAndParams();
    const [selectedGraphType, setSelectedGraphType] = useState<EGraphType>(
        params.graphType ? Number(params.graphType) : DEFAULT_GRAPH_TYPE,
    );
    const [selectedGranularity, setSelectedGranularity] = useState<EGraphGranularities>(
        getTimeGranularityForApi(params.timeGranularity),
    );
    const [dateFilter, setDateFilter] = useState<IDateFilter>();
    const { userGroups, sharedGroups } = useAllKeywordGroups();
    const selectedGroup = useMemo(() => {
        if (isKeywordsGroup) {
            return userGroups.find((group) => group.Id === params.keyword);
        }
        return sharedGroups.find((group) => group.Id === params.keyword);
    }, [isKeywordsGroup, userGroups, sharedGroups, params.keyword]);

    const is28Days = params.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const subtitleFilters = useSubtitleFilters("country", "webSource");
    const durationData = DurationService.getDurationData(params.duration).raw;

    const graphDurations = [
        is28Days ? BasicDurations.LAST_TWENTY_EIGHT_DAYS : BasicDurations.LAST_MONTH,
        BasicDurations.LAST_THREE_MONTHS,
        BasicDurations.LAST_SIX_MONTHS,
        BasicDurations.LAST_THIRTEEN_MONTHS,
    ];
    const getIndexGraphDuration = getIndexOfList(graphDurations, params.graphDuration);
    const [selectedGraphDurationIndex, setSelectedGraphDurationIndex] = useState(
        params.graphDuration ? (getIndexGraphDuration === -1 ? 0 : getIndexGraphDuration) : 0,
    );
    const excelAllowed = swSettings.current.resources.IsExcelAllowed;
    const { percentageData, transformedData, isLoading, handleSiteToggle, handleGraphTypeChange, error, excelLink } =
        useGraphData(
            getTimeGranularityForApi(params.timeGranularity ?? graphGranularityToString[selectedGranularity]),
            params.graphDuration ?? graphDurations[selectedGraphDurationIndex],
            filters,
            endpoint,
            excelEndpoint,
        );

    const shouldReturnNoData = (items: ITransformedDataPoint[]): boolean => {
        return items.every((item) => item.isOther || item.isTotal) || items.length === 0;
    };

    const ErrorMessage = error
        ? {
              title: translate("keyword.analysis.graph.error.title"),
              subtitle: translate("keyword.analysis.graph.error.subtitle"),
          }
        : {
              title: translate("keyword.analysis.overview.nodata"),
              subtitle: translate("keyword.analysis.overview.nodata.subtitle"),
          };

    useEffect(() => {
        if (transformedData.length > 0) {
            const { from, to } = durationData;

            const dateFilter = {
                filter: "date",
                value: {
                    from: from.valueOf(),
                    to: to.valueOf(),
                },
                getFilterValue: ({ filter }) => {
                    const durationKey = graphDurations[selectedGraphDurationIndex];
                    const { unit, count } = DurationService.parsedDurationParam(durationKey);
                    const opUnit: OpUnitType = unit === "D" ? "day" : unit === "W" ? "week" : "month";

                    if (is28Days) {
                        return translate("keywordanalysis.organic2.clicks.over.time.duration.text", {
                            duration: `${count} ${capitalize(opUnit)}`,
                            lastDate: dayjs(filter.value.to).format(shortMonthAndDay),
                        });
                    }

                    return formatDate(dayjs(filter.value.to).subtract(count, opUnit).valueOf(), filter.value.to);
                },
            };
            setDateFilter(dateFilter);
        }
    }, [transformedData]);

    const GraphTooltipFormatter = useGraphTooltip(
        percentageData,
        transformedData,
        GRAPH_TYPES[selectedGraphType],
        selectedGranularity,
        durationData,
        filters.sourceType ?? "",
        graphTitles,
    );

    const handlePngDownload = () => {
        pngDownload(graphContainerRef.current, translate("keywordanalysis.organic2.clicks.over.time.title.tooltip"));
    };

    const handlePNGTracking = () => {
        allTrackers.trackEvent("Download", "submit-ok", `Organic Clicks Over Time/PNG`);
    };

    const handleGranularityChange = (granularity: EGraphGranularities) => {
        allTrackers.dispatchTrackEvent(
            "Switch",
            "switch",
            `Graph Granularity Change/${GRAPH_GRANULARITY_TO_TRACKING_NAME[granularity]}`,
        );
        setSelectedGranularity(granularity);
        proNavigator.updateParams({
            [TIME_GRANULARITY]: graphGranularityToString[granularity],
        });
    };

    const onGraphTypeChange = ({ id: type }: { id: EGraphType }) => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "switch",
            `Traffic Competitors Graph/${GRAPH_TYPE_TO_TRACKING_NAME[EGraphType[type]]}`,
        );
        setSelectedGraphType(type);
        // if there is no data, don't recalculate the charts data
        if (transformedData.length) {
            handleGraphTypeChange(GRAPH_TYPES[type]);
        }
        proNavigator.updateParams({ graphType: type });
    };

    const handleGraphDurationChange = (index: number) => {
        const graphDuration = graphDurations[index];
        allTrackers.dispatchTrackEvent("Switch", "switch", `Graph History Change/${graphDuration}`);
        if (isDurationSwitcherLocked(graphDuration)) {
            openHooksModalV5("keywordResearch");
            return;
        }
        const granularityParamUpdate = updateGraphGranularityValue(
            graphDuration,
            graphGranularityToString[selectedGranularity] as GranularityNames,
            TIME_GRANULARITY,
        );
        if (granularityParamUpdate) {
            const updatedGranularity = granularityParamUpdate[TIME_GRANULARITY];
            setSelectedGranularity(getTimeGranularityForApi(updatedGranularity));
        }
        setSelectedGraphDurationIndex(index);
        proNavigator.updateParams({
            graphDuration,
            ...(granularityParamUpdate && granularityParamUpdate),
        });
    };

    const getChartData = () => {
        switch (Number(selectedGraphType)) {
            case EGraphType["chart-stacked-share"]:
                return percentageData;
            case EGraphType.overview:
                return transformedData;
            default:
                return transformedData.filter((dp: ITransformedDataPoint) => !dp.isOther && !dp.isTotal);
        }
    };
    return (
        <MetricContainerStyled
            height="unset"
            padding="12px 24px 24px"
        >
            <div ref={graphContainerRef}>
                <BoxTitle
                    Container={BoxTitleContainer}
                    customElement={
                        <GraphHeaderActions>
                            <TooltipStyles />

                            <DownloadButtonMenu
                                PNG={true}
                                handleDownloadFunction={handlePngDownload}
                                trackingFunction={handlePNGTracking}
                            />
                            <WeShare
                                chartRef={graphContainerRef.current}
                                width={(graphContainerRef.current?.getBoundingClientRect?.().width ?? 0) + 50}
                                height={(graphContainerRef.current?.getBoundingClientRect?.().height ?? 0) + 100}
                                title={`${
                                    isKeywordsGroup ? `Group "${selectedGroup?.Name}"` : `Keyword "${params.keyword}"`
                                } ${translate("keywords.research.common.traffic.chart.header")}`}
                                description={translate("keywords.research.common.traffic.chart.header")}
                            />
                            <AddToDashboard
                                type={DASHBOARD_TYPE}
                                metric={dashboardMetric}
                                modelType="fromKeyword"
                                webSource={params.webSource}
                                filters={{
                                    sourceType: filters.sourceType,
                                    graphDuration: graphDurations[selectedGraphDurationIndex],
                                    graphType: selectedGraphType,
                                    timeGranularity: graphGranularityToString[selectedGranularity],
                                    duration: params.duration,
                                    domains: params.domains,
                                    category: params.category,
                                    funcFlag: params.funcFlag,
                                }}
                            />
                            {excelEndpoint ? (
                                <DownloadButtonMenu
                                    Excel={true}
                                    downloadUrl={excelLink}
                                    excelLocked={!excelAllowed}
                                />
                            ) : null}
                        </GraphHeaderActions>
                    }
                >
                    <ClicksOverTimeTitle>
                        {`keywordanalysis.${SourceTypeMap[params.selectedPageTab]}2.clicks.over.time.title`}
                    </ClicksOverTimeTitle>
                </BoxTitle>
                {dateFilter ? <BoxSubtitleStyled filters={[dateFilter, ...subtitleFilters]} /> : null}
                <Divider />
                <ChartFilterBar>
                    <Switcher
                        onItemClick={handleGraphDurationChange}
                        selectedIndex={selectedGraphDurationIndex}
                        customClass="CircleSwitcher"
                    >
                        {graphDurations.map((graphDuration) => (
                            <CircleSwitcherItemStyled
                                key={graphDuration}
                                disabled={isDurationSwitcherDisabled(graphDuration)}
                                isLocked={isDurationSwitcherLocked(graphDuration)}
                            >
                                {graphDuration.toUpperCase()}
                            </CircleSwitcherItemStyled>
                        ))}
                    </Switcher>
                    <Switcher
                        onItemClick={handleGranularityChange}
                        selectedIndex={selectedGranularity}
                        customClass="CircleSwitcher"
                    >
                        {GRAPH_GRANULARITIES.map((granularity) => (
                            <CircleSwitcherItemStyled
                                key={granularity}
                                disabled={isGranularityDisabled(granularity, selectedGraphDurationIndex)}
                            >
                                {translate(granularity)}
                            </CircleSwitcherItemStyled>
                        ))}
                    </Switcher>
                    <Dropdown
                        appendTo={"body"}
                        dropdownPopupPlacement={"ontop-left"}
                        selectedIds={{ [selectedGraphType]: true }}
                        width={180}
                        onClick={onGraphTypeChange}
                        itemsComponent={(props) => (
                            <SimpleDropdownItemStyled {...props}>
                                <SWReactIcons
                                    iconName={props.icon}
                                    size="xs"
                                />
                                {props.children}
                            </SimpleDropdownItemStyled>
                        )}
                    >
                        {[
                            <DropdownButton
                                key="item0"
                                width={180}
                            >
                                <DropdownButtonIconWrapper>
                                    <SWReactIcons
                                        iconName={GRAPH_TYPES[selectedGraphType]}
                                        size="xs"
                                    />
                                </DropdownButtonIconWrapper>
                                {translate(graphTypeTooltips[EGraphType[selectedGraphType]]?.title)}
                            </DropdownButton>,
                            ...GRAPH_TYPES.map((graphType, id) => ({
                                id,
                                text: translate(graphTypeTooltips[graphType].title),
                                icon: graphType,
                            })),
                        ]}
                    </Dropdown>
                </ChartFilterBar>
                <FlexRow style={{ alignItems: "center" }}>
                    {isLoading ? (
                        <LoaderContainer>
                            <Loader />
                        </LoaderContainer>
                    ) : !transformedData.length || shouldReturnNoData(transformedData) || error ? (
                        <ChartContainerNoData>
                            <NoDataLandscape
                                title={ErrorMessage.title}
                                subtitle={ErrorMessage.subtitle}
                            />
                        </ChartContainerNoData>
                    ) : (
                        <Container>
                            <ResizableContainer>
                                <ChartContainerWrapper>
                                    <Chart
                                        type={
                                            Number(selectedGraphType) === EGraphType.overview
                                                ? chartTypes.LINE
                                                : chartTypes.AREA
                                        }
                                        config={getChartConfig(
                                            selectedGraphType === EGraphType["chart-stacked-share"],
                                            selectedGranularity,
                                            is28Days,
                                            graphDurations[selectedGraphDurationIndex],
                                            GraphTooltipFormatter,
                                        )}
                                        data={getChartData()}
                                    />
                                </ChartContainerWrapper>
                            </ResizableContainer>
                            <LegendsContainer>
                                <ChartLabels
                                    selectedGranularity={selectedGranularity}
                                    sites={transformedData}
                                    onSiteToggle={(series) => series && handleSiteToggle(series)}
                                    extraFieldType={graphTypeToExtraFiledType[selectedGraphType]}
                                    displayTooltipOnLegendOver={displayTooltipOnLegendOver}
                                    graphTitles={graphTitles}
                                />
                            </LegendsContainer>
                        </Container>
                    )}
                </FlexRow>
            </div>
        </MetricContainerStyled>
    );
};
